import type { Project } from '../../store/Project';
import { InventoryAsset, SupportCasesItem } from '../../types';
import { callExternalApi } from '../callExternalApi';
import type { WorkflowOrder } from '../Orders/ordersApi';
import type { RecordSection } from '../../components/Compounds/RelatedRecordsTable/relatedRecords';

export type LocationRole = 'install' | 'a' | 'z';

export interface LocationAddressInfo {
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  number?: string | null;
  locationName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface LocationRow {
  location_key: string;
  account_id: string;
  location_code: string | null;
  location_name: string | null;
  customer_location_name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  addressInfo: LocationAddressInfo;
  roles: LocationRole[];
  address_source?: 'billing' | null;
  active_inventory_count: number | null;
  active_case_count?: number | null;
  active_order_count: number | null;
  active_project_count?: number | null;
}

export const getLocations = async (accountIds?: string[]) => {
  if (!accountIds?.length) return { data: null, error: { message: 'Must provide account IDs to fetch locations' } };
  return await callExternalApi<LocationRow[]>({
    url: `api/locations?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export type LocationRecordSection<T> = RecordSection<T>;

export type LocationInventoryRecord = InventoryAsset & {
  location_roles: LocationRole[];
  active: boolean | null;
};

export type LocationCaseRecord = SupportCasesItem & { active: boolean | null };

export type LocationOrderRecord = WorkflowOrder & { active: boolean | null };

export type LocationProjectRecord = Project & { expected_completion: string | null; active: boolean | null };

export interface LocationDetail extends LocationRow {
  inventory: LocationRecordSection<LocationInventoryRecord>;
  cases?: LocationRecordSection<LocationCaseRecord>;
  orders: LocationRecordSection<LocationOrderRecord> | null;
  projects?: LocationRecordSection<LocationProjectRecord>;
}

export const getLocationDetail = async (locationKey: string, accountIds?: string[]) => {
  if (!accountIds?.length) return { data: null, error: { message: 'Must provide account IDs to fetch a location' } };
  return await callExternalApi<LocationDetail>({
    url: `api/locations/${encodeURIComponent(locationKey)}?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};
